import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
    AttachmentHandler,
    ChangeDetails,
    FlyFreelyError,
    FlyFreelyLoggingService,
    GqlSortField,
    PersonsOrganisationDto,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { ColumnSortPreferences } from '@flyfreely-portal-ui/flyfreely-table';
import {
    CreateTaskCommand,
    DateRange,
    GeneralTask,
    GeneralTasksService,
    TaskComment,
    TaskPriority,
    TaskStatus,
    UpdateTaskCommand
} from 'libs/flyfreely/src/lib/services/generalTasks.service';
import { Delta } from 'quill';
import { BehaviorSubject, ReplaySubject, Subject, tap, throwError } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import Type = ChangeDetails.Type;

export const formatGeneralTasksPriority = {
    [TaskPriority.LOW]: 'Low',
    [TaskPriority.MEDIUM]: 'Medium',
    [TaskPriority.HIGH]: 'High'
};
export const formatGeneralTasksStatuses = {
    [TaskStatus.IN_PROGRESS]: 'In Progress',
    [TaskStatus.COMPLETED]: 'Completed',
    [TaskStatus.CANCELLED]: 'Cancelled',
    [TaskStatus.TODO]: 'Todo',
    [TaskStatus.NEW]: 'Draft'
};

@Injectable({
    providedIn: 'root'
})
export class GeneralTasksDataService implements OnDestroy {
    currentOrganisation: PersonsOrganisationDto;

    private generalTasksSource = new ReplaySubject<GeneralTask[]>(1);
    private workingSource = new BehaviorSubject<boolean>(false);
    private totalItemsSource = new ReplaySubject<number>(1);
    private currentPageSource = new BehaviorSubject<number>(0);
    private changeSource = new Subject<ChangeDetails<GeneralTask>>();
    private commentChangeSource = new Subject<ChangeDetails<TaskComment>>();

    change$ = this.changeSource.asObservable();
    working$ = this.workingSource.asObservable();
    commentChange$ = this.commentChangeSource.asObservable();
    generalTasks$ = this.generalTasksSource.asObservable();
    currentPage$ = this.currentPageSource.asObservable();
    totalItems$ = this.totalItemsSource.asObservable();

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private generalTasksService: GeneralTasksService,
        private logging: FlyFreelyLoggingService,
        private http: HttpClient
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.workingSource.complete();
        this.currentPageSource.complete();
        this.totalItemsSource.complete();
        this.generalTasksSource.complete();
        this.changeSource.complete();
        this.commentChangeSource.complete();
    }

    findTaskById(taskId: number) {
        return this.generalTasksService.findTaskById(taskId).pipe(
            catchError((error: FlyFreelyError) => {
                this.logging.error(
                    error,
                    `Error while finding task: ${error.message}`
                );
                return throwError(() => error.message ?? 'Entry is invalid');
            })
        );
    }

    updateTaskTable({
        organisationId,
        page,
        limit,
        sorting,
        text,
        summary,
        dueDate,
        reporterId,
        assigneeId,
        priority,
        status
    }: {
        organisationId: number;
        page: number;
        limit: number;
        sorting: ColumnSortPreferences;
        text?: string;
        summary?: string;
        dueDate?: DateRange;
        reporterId?: number;
        assigneeId?: number;
        priority?: TaskPriority[];
        status?: TaskStatus[];
    }) {
        const sortFields: GqlSortField = {
            field: sorting?.column ?? null,
            order: sorting?.ascending ? 'ASC' : 'DESC'
        };

        this.generalTasksService
            .findTasks(
                organisationId,
                {
                    page: page,
                    pageSize: limit,
                    sortFields: sorting != null ? [sortFields] : []
                },
                text,
                summary,
                dueDate,
                reporterId,
                assigneeId,
                priority,
                status
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: tasks => {
                    this.totalItemsSource.next(tasks.count);
                    this.currentPageSource.next(page);
                    this.generalTasksSource.next(tasks.results);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while refreshing tasks: ${error.message}`
                    );
                    this.generalTasksSource.next([]);
                    this.totalItemsSource.next(0);
                    this.currentPageSource.next(0);
                }
            })
            .add(this.workTracker.createTracker());
    }

    newTask(organisationId: number) {
        const doneWorking = this.workTracker.createTracker();
        return this.generalTasksService.newTask(organisationId).pipe(
            tap({
                finalize: doneWorking
            }),
            takeUntil(this.ngUnsubscribe$)
        );
    }

    createTask(createTaskPara: CreateTaskCommand) {
        const doneWorking = this.workTracker.createTracker();
        return this.generalTasksService.createTask(createTaskPara).pipe(
            tap({
                next: result => {
                    this.changeSource.next({
                        type: ChangeDetails.Type.CREATE,
                        changedId: result.id,
                        result
                    });
                },
                finalize: doneWorking
            }),
            takeUntil(this.ngUnsubscribe$)
        );
    }

    updateTask(updateTaskPara: UpdateTaskCommand) {
        const doneWorking = this.workTracker.createTracker();
        return this.generalTasksService.updateTask(updateTaskPara).pipe(
            tap({
                next: result => {
                    this.changeSource.next({
                        type: ChangeDetails.Type.UPDATE,
                        changedId: result.id,
                        result
                    });
                },
                finalize: doneWorking
            }),
            takeUntil(this.ngUnsubscribe$)
        );
    }

    deleteTask(taskId: number) {
        const doneWorking = this.workTracker.createTracker();
        return this.generalTasksService.deleteTask(taskId).pipe(
            tap({
                next: result => {
                    this.changeSource.next({
                        type: ChangeDetails.Type.DELETE,
                        changedId: taskId
                    });
                },
                finalize: doneWorking
            }),
            takeUntil(this.ngUnsubscribe$)
        );
    }

    attachmentHandler(taskId: number, organisationId: number) {
        return new AttachmentHandler(
            this.http,
            `/webapi/tasks/${taskId}/attachments`,
            false,
            organisationId
        );
    }

    createComment(taskId: number, content: Delta) {
        const doneWorking = this.workTracker.createTracker();
        return this.generalTasksService.createTaskComment(taskId, content).pipe(
            tap({
                next: result => {
                    this.commentChangeSource.next({
                        type: Type.CREATE,
                        changedId: result.id
                    });
                },
                finalize: doneWorking
            }),
            takeUntil(this.ngUnsubscribe$)
        );
    }

    updateComment(taskId: number, commentId: number, content: Delta) {
        const doneWorking = this.workTracker.createTracker();
        return this.generalTasksService
            .updateTaskComment(taskId, commentId, content)
            .pipe(
                tap({
                    next: result => {
                        this.commentChangeSource.next({
                            type: Type.UPDATE,
                            changedId: result.id
                        });
                    },
                    finalize: doneWorking
                }),
                takeUntil(this.ngUnsubscribe$)
            );
    }

    deleteComment(taskId: number, commentId: number) {
        const doneWorking = this.workTracker.createTracker();
        return this.generalTasksService
            .deleteTaskComment(taskId, commentId)
            .pipe(
                tap({
                    next: result => {
                        this.commentChangeSource.next({
                            type: Type.DELETE,
                            changedId: taskId
                        });
                    },
                    finalize: doneWorking
                }),
                takeUntil(this.ngUnsubscribe$)
            );
    }

    findComments(taskId: number) {
        return this.generalTasksService.findTaskComments(taskId);
    }
}
